import axios from 'axios';
import apiAxios from '../axiosConfig';
import { getMsalAuth } from '../AuthProvider';
import type { CurrentUserInfo } from './usersService';

const USAGE_LOGGED_KEY = 'usage_logged';
const TOOL_ACTION_SESSION_ID_KEY = 'tool_action_session_id';
const TOOL_ACTION_SESSION_STARTED_AT_KEY = 'tool_action_session_started_at';
const TOOL_ACTION_SESSION_LAST_ACTIVITY_AT_KEY = 'tool_action_session_last_activity_at';
const TOOL_ACTION_SESSION_START_LOGGED_KEY = 'tool_action_session_start_logged';
const DEFAULT_SESSION_TIMEOUT_MINUTES = 30;

export type ToolActionName =
  | 'session_start'
  | 'session_end'
  | 'session_timeout'
  | 'codesheet_view'
  | 'system_error';

export type TelemetryIdentity = Partial<Pick<CurrentUserInfo, 'emailAddress' | 'name'>> & {
  email?: string | null;
  userName?: string | null;
  username?: string | null;
};

export interface ToolActionPayload {
  UserName: string;
  MachineName: string;
  ExecutionDateTime: string;
  AppExecuted: string;
  AppAction?: string;
  AppDescription?: string;
  AppValue?: string;
  JSON?: string;
}

export interface ToolActionEventInput {
  identity?: TelemetryIdentity | null;
  action: ToolActionName;
  description?: string;
  value?: string;
  context?: Record<string, unknown>;
  executionDateTime?: Date;
}

export interface SystemErrorTelemetryInput {
  action: string;
  error: unknown;
  resource?: string;
  resourceId?: string | null;
  context?: Record<string, unknown>;
}

export interface ToolActionSessionSnapshot {
  sessionId: string;
  startedAt: string;
  lastActivityAt: string;
}

let missingEndpointWarned = false;

function getBrowserWindow(): Window | null {
  return typeof window === 'undefined' ? null : window;
}

function getBrowserNavigator(): Navigator | null {
  return typeof navigator === 'undefined' ? null : navigator;
}

function getToolActionEndpoint(): string {
  return String(process.env.REACT_APP_TOOL_ACTION_API_URL || '').trim();
}

function getAppExecuted(): string {
  return String(process.env.REACT_APP_FEEDBACK_SOURCE_APP || '').trim() || 'CodeSheetApp';
}

function warnMissingEndpoint(): void {
  if (missingEndpointWarned || process.env.NODE_ENV !== 'development') return;
  missingEndpointWarned = true;
  console.warn('Tool action telemetry endpoint is not configured.');
}

function readStorageValue(key: string): string | null {
  try {
    return getBrowserWindow()?.sessionStorage?.getItem(key) || null;
  } catch (_error) {
    return null;
  }
}

function writeStorageValue(key: string, value: string): void {
  try {
    getBrowserWindow()?.sessionStorage?.setItem(key, value);
  } catch (_error) {
    // Ignore storage failures; telemetry should never affect app workflows.
  }
}

function removeStorageValue(key: string): void {
  try {
    getBrowserWindow()?.sessionStorage?.removeItem(key);
  } catch (_error) {
    // Ignore storage failures; telemetry should never affect app workflows.
  }
}

function createSessionId(): string {
  const cryptoApi = getBrowserWindow()?.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }

  return `session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeText(value: unknown): string {
  return String(value ?? '').trim();
}

function getMsalUserName(): string {
  const { accounts } = getMsalAuth();
  const account = accounts?.[0];
  const email = normalizeText(account?.username);
  const name = normalizeText(account?.name);

  if (name && email && name.toLowerCase() !== email.toLowerCase()) {
    return `${name} <${email}>`;
  }

  return email || name;
}

export function resolveTelemetryUserName(identity?: TelemetryIdentity | null): string {
  const email = normalizeText(identity?.emailAddress || identity?.email || identity?.username);
  const name = normalizeText(identity?.name || identity?.userName);

  if (name && email && name.toLowerCase() !== email.toLowerCase()) {
    return `${name} <${email}>`;
  }

  return email || name || getMsalUserName() || 'unknown-user';
}

function getMachineName(): string {
  return normalizeText(getBrowserWindow()?.location?.hostname) || 'unknown-browser';
}

function getPageUrl(): string | null {
  return getBrowserWindow()?.location?.href || null;
}

function getBrowserContext(): Record<string, unknown> {
  const browserNavigator = getBrowserNavigator();
  return {
    pageUrl: getPageUrl(),
    userAgent: browserNavigator?.userAgent || null,
    language: browserNavigator?.language || null,
    platform: browserNavigator?.platform || null,
    timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : null,
  };
}

function safeJsonStringify(value: Record<string, unknown>): string {
  try {
    return JSON.stringify(value);
  } catch (_error) {
    return JSON.stringify({ serializationError: true });
  }
}

export function serializeTelemetryError(error: unknown): Record<string, unknown> {
  const err = error as {
    name?: unknown;
    message?: unknown;
    code?: unknown;
    stack?: unknown;
    config?: {
      url?: unknown;
      method?: unknown;
    };
    response?: {
      status?: unknown;
      statusText?: unknown;
      data?: {
        message?: unknown;
        title?: unknown;
        errors?: unknown;
        Errors?: unknown;
      };
    };
  };

  const responseData = err?.response?.data;
  return {
    name: normalizeText(err?.name) || (error instanceof Error ? error.name : null),
    message: normalizeText(err?.message) || (error instanceof Error ? error.message : String(error || 'Unknown error')),
    code: err?.code ?? null,
    status: err?.response?.status ?? null,
    statusText: err?.response?.statusText ?? null,
    method: normalizeText(err?.config?.method).toUpperCase() || null,
    url: normalizeText(err?.config?.url) || null,
    responseMessage: normalizeText(responseData?.message || responseData?.title) || null,
    responseErrors: responseData?.errors || responseData?.Errors || null,
  };
}

export function buildToolActionPayload(input: ToolActionEventInput): ToolActionPayload {
  const session = getTelemetrySessionSnapshot();
  const context = {
    ...getBrowserContext(),
    sessionId: session?.sessionId || null,
    sessionStartedAt: session?.startedAt || null,
    sessionLastActivityAt: session?.lastActivityAt || null,
    ...(input.context || {}),
  };

  const payload: ToolActionPayload = {
    UserName: resolveTelemetryUserName(input.identity),
    MachineName: getMachineName(),
    ExecutionDateTime: (input.executionDateTime || new Date()).toISOString(),
    AppExecuted: getAppExecuted(),
    AppAction: input.action,
    JSON: safeJsonStringify(context),
  };

  if (input.description) payload.AppDescription = input.description;
  if (input.value) payload.AppValue = input.value;

  return payload;
}

export async function postToolActionEvent(input: ToolActionEventInput): Promise<void> {
  const endpoint = getToolActionEndpoint();
  if (!endpoint) {
    warnMissingEndpoint();
    return;
  }

  try {
    await axios.post(endpoint, buildToolActionPayload(input), {
      headers: { 'Content-Type': 'application/json' },
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to log tool action telemetry', error);
    }
  }
}

export function postToolActionEventOnUnload(input: ToolActionEventInput): void {
  const endpoint = getToolActionEndpoint();
  if (!endpoint) {
    warnMissingEndpoint();
    return;
  }

  const payload = buildToolActionPayload(input);
  const body = JSON.stringify(payload);
  const browserNavigator = getBrowserNavigator();

  try {
    if (browserNavigator && typeof browserNavigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (browserNavigator.sendBeacon(endpoint, blob)) return;
    }

    if (typeof fetch === 'function') {
      void fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => undefined);
    }
  } catch (_error) {
    // Ignore unload telemetry failures.
  }
}

export function getSessionTimeoutMs(): number {
  const configuredMinutes = Number(process.env.REACT_APP_TOOL_ACTION_SESSION_TIMEOUT_MINUTES);
  const minutes = Number.isFinite(configuredMinutes) && configuredMinutes > 0
    ? configuredMinutes
    : DEFAULT_SESSION_TIMEOUT_MINUTES;
  return minutes * 60 * 1000;
}

export function getTelemetrySessionSnapshot(): ToolActionSessionSnapshot | null {
  const sessionId = readStorageValue(TOOL_ACTION_SESSION_ID_KEY);
  const startedAt = readStorageValue(TOOL_ACTION_SESSION_STARTED_AT_KEY);
  const lastActivityAt = readStorageValue(TOOL_ACTION_SESSION_LAST_ACTIVITY_AT_KEY) || startedAt;

  if (!sessionId || !startedAt || !lastActivityAt) return null;
  return { sessionId, startedAt, lastActivityAt };
}

export function ensureTelemetrySession(): ToolActionSessionSnapshot {
  const existing = getTelemetrySessionSnapshot();
  if (existing) return existing;

  const now = new Date().toISOString();
  const session = {
    sessionId: createSessionId(),
    startedAt: now,
    lastActivityAt: now,
  };

  writeStorageValue(TOOL_ACTION_SESSION_ID_KEY, session.sessionId);
  writeStorageValue(TOOL_ACTION_SESSION_STARTED_AT_KEY, session.startedAt);
  writeStorageValue(TOOL_ACTION_SESSION_LAST_ACTIVITY_AT_KEY, session.lastActivityAt);
  return session;
}

export function touchTelemetrySession(activityDate = new Date()): ToolActionSessionSnapshot {
  const session = ensureTelemetrySession();
  const lastActivityAt = activityDate.toISOString();
  writeStorageValue(TOOL_ACTION_SESSION_LAST_ACTIVITY_AT_KEY, lastActivityAt);
  return { ...session, lastActivityAt };
}

export function clearTelemetrySession(): void {
  removeStorageValue(TOOL_ACTION_SESSION_ID_KEY);
  removeStorageValue(TOOL_ACTION_SESSION_STARTED_AT_KEY);
  removeStorageValue(TOOL_ACTION_SESSION_LAST_ACTIVITY_AT_KEY);
  removeStorageValue(TOOL_ACTION_SESSION_START_LOGGED_KEY);
}

export function startTelemetrySession(identity?: TelemetryIdentity | null): ToolActionSessionSnapshot {
  const session = ensureTelemetrySession();
  if (!readStorageValue(TOOL_ACTION_SESSION_START_LOGGED_KEY)) {
    writeStorageValue(TOOL_ACTION_SESSION_START_LOGGED_KEY, 'true');
    void postToolActionEvent({
      identity,
      action: 'session_start',
      description: 'User session started',
      context: {
        sessionId: session.sessionId,
        sessionStartedAt: session.startedAt,
      },
    });
  }

  return session;
}

export function endTelemetrySession(
  identity: TelemetryIdentity | null | undefined,
  action: Extract<ToolActionName, 'session_end' | 'session_timeout'>,
  transport: 'async' | 'unload' = 'async',
  endedAtDate = new Date()
): void {
  const session = getTelemetrySessionSnapshot();
  if (!session) return;

  const endedAt = endedAtDate.toISOString();
  const lastActivityAt = session.lastActivityAt || endedAt;
  const durationMs = Math.max(0, new Date(lastActivityAt).getTime() - new Date(session.startedAt).getTime());
  const input: ToolActionEventInput = {
    identity,
    action,
    description: action === 'session_timeout' ? 'User session timed out' : 'User session ended',
    value: `${Math.round(durationMs / 1000)} seconds`,
    executionDateTime: endedAtDate,
    context: {
      sessionId: session.sessionId,
      sessionStartedAt: session.startedAt,
      sessionEndedAt: endedAt,
      sessionLastActivityAt: lastActivityAt,
      sessionDurationMs: durationMs,
    },
  };

  if (transport === 'unload') {
    postToolActionEventOnUnload(input);
  } else {
    void postToolActionEvent(input);
  }

  clearTelemetrySession();
}

export function logCodesheetView(
  identity: TelemetryIdentity | null | undefined,
  codesheetId: string,
  context?: Record<string, unknown>
): void {
  const format = normalizeText(context?.format);
  const businessUnit = normalizeText(context?.businessUnit);
  const descriptionDetails = [
    format ? `Format ${format}` : '',
    businessUnit ? `BU ${businessUnit}` : '',
  ].filter(Boolean).join(' | ');

  void postToolActionEvent({
    identity,
    action: 'codesheet_view',
    description: descriptionDetails
      ? `Code Sheet opened or viewed (${descriptionDetails})`
      : 'Code Sheet opened or viewed',
    value: format ? `Format: ${format}; Id: ${codesheetId}` : codesheetId,
    context: {
      resource: 'codesheet',
      resourceId: codesheetId,
      codesheetIdentifier: codesheetId,
      ...(context || {}),
    },
  });
}

export function logSystemError(
  identity: TelemetryIdentity | null | undefined,
  input: SystemErrorTelemetryInput
): void {
  void postToolActionEvent({
    identity,
    action: 'system_error',
    description: input.action,
    value: input.resourceId || input.resource || input.action,
    context: {
      resource: input.resource || null,
      resourceId: input.resourceId || null,
      failedAction: input.action,
      error: serializeTelemetryError(input.error),
      ...(input.context || {}),
    },
  });
}

/**
 * Log usage for the current session.
 * Uses sessionStorage to ensure it only fires once per browser session
 * (survives page refresh, resets when tab/browser is closed).
 */
export async function logUsage(): Promise<void> {
  if (sessionStorage.getItem(USAGE_LOGGED_KEY)) {
    return;
  }

  try {
    await apiAxios.post('/Usage/log');
    sessionStorage.setItem(USAGE_LOGGED_KEY, 'true');
  } catch (e) {
    console.error('Failed to log usage', e);
  }
}
